import React, { ReactNode, useState } from 'react';

import { Accordion, AccordionProps, Badge, Button, Group, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconInfoSquareRounded } from '@tabler/icons-react';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloButton from 'components/KiloButton';
import { KiloButtonVariant } from 'components/KiloButton/KiloButton';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';

import classes from './kiloAccordion.module.css';

type KiloAccordionVariant = 'default' | 'kiosk' | 'shoppingCart'; // add more as needed

type KiloAccordionProps = AccordionProps & {
  rootVariantType?: KiloAccordionVariant;
  children: ReactNode;
  title: string;
  showIcon?: boolean;
  controlsChildren?: ReactNode;
  titleRootVariantType?: KiloButtonVariant;
  onTitleClick: () => void;
};

const variantClassMap: Record<KiloAccordionVariant, Partial<typeof classes>> = {
  default: {}, // Default styles are already applied in `classes`
  kiosk: {
    control: classes.kioskControl,
    item: classes.kioskItem,
    content: classes.kioskContent,
    chevron: classes.kioskChevron,
    label: classes.KioskLabel,
    icon: classes.KioskIcon,
  },
  shoppingCart: {
    content: classes.shoppingCartContent,
    label: classes.shoppingCartLabel,
    chevron: classes.shoppingCartChevron,
  },
};

const KiloAccordion = (props: KiloAccordionProps): JSX.Element => {
  const {
    title,
    children,
    rootVariantType = 'default',
    showIcon,
    chevronPosition = 'right',
    controlsChildren,
    titleRootVariantType = 'flat',
    onTitleClick,
    ...rest
  } = props;
  const [opened, setOpened] = useState<string | null>(null);

  const variantClasses = variantClassMap[rootVariantType];

  return (
    <Accordion
      {...rest}
      chevronPosition={chevronPosition}
      variant='default'
      value={opened}
      onChange={setOpened}
      classNames={{
        ...classes,
        ...variantClasses,
      }}
    >
      <Accordion.Item value={title} key={title} onClick={e => e.stopPropagation()}>
        <Accordion.Control>
          <Group noWrap position='apart'>
            <KiloButton
              onClick={e => {
                e.stopPropagation();
                setOpened(opened === title ? null : title);
                onTitleClick?.();
              }}
              rootVariantType={titleRootVariantType}
              variant='subtle'
            >
              {title}
            </KiloButton>

            {opened === title && showIcon && (
              <KiloTooltip
                wrapperDivClassVariantType='defaultAbsolute'
                label={TOOLTIP_MESSAGES.CATEGORIES_EXTRA_INFO}
                withArrow
                position='left-start'
              >
                <KiloActionIcon rootVariantType='defaultAbsolute' variant='subtle'>
                  <IconInfoSquareRounded size='1.4rem' />
                </KiloActionIcon>
              </KiloTooltip>
            )}
            {controlsChildren}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default KiloAccordion;

// ---------------------------------------------------------------------------
// Checkout variant — a controlled, multi-item step accordion (used by the
// public sales-portal checkout). Unlike the single-item KiloAccordion above,
// items are driven by data and open/close is fully controlled by the caller:
// completed steps collapse to a summary with an Edit affordance, locked steps
// cannot be opened, and invalidated steps surface an inline warning.
// ---------------------------------------------------------------------------

export interface KiloCheckoutAccordionItem {
  value: string;
  label: string;
  stepNumber: number;
  completed: boolean;
  locked: boolean;
  summary?: ReactNode;
  warning?: string | null;
  onEdit?: () => void;
  children: ReactNode;
}

interface KiloCheckoutAccordionProps {
  items: KiloCheckoutAccordionItem[];
  value: string | null;
  onChange: (value: string | null) => void;
}

export const KiloCheckoutAccordion = ({
  items,
  value,
  onChange,
}: KiloCheckoutAccordionProps): JSX.Element => {
  return (
    <Accordion
      variant='separated'
      chevron={null}
      value={value}
      onChange={next => {
        const item = items.find(candidate => candidate.value === next);
        if (item?.locked) return;
        onChange(next);
      }}
      classNames={{
        item: classes.checkoutItem,
        control: classes.checkoutControl,
        content: classes.checkoutContent,
        label: classes.checkoutLabel,
      }}
    >
      {items.map(item => {
        const isOpen = value === item.value;
        return (
          <Accordion.Item value={item.value} key={item.value} data-cy={`checkoutStep-${item.value}`}>
            <Accordion.Control disabled={item.locked && !item.completed && !isOpen}>
              <Group position='apart' noWrap>
                <Group spacing='sm' noWrap>
                  {item.completed && !isOpen ? (
                    <ThemeIcon
                      radius='xl'
                      size='md'
                      color='green'
                      data-cy={`checkoutStepComplete-${item.value}`}
                    >
                      <IconCheck size='0.9rem' />
                    </ThemeIcon>
                  ) : (
                    <Badge radius='xl' size='lg' variant={isOpen ? 'filled' : 'outline'}>
                      {item.stepNumber}
                    </Badge>
                  )}
                  <div>
                    <Text fw={600}>{item.label}</Text>
                    {item.completed && !isOpen && item.summary && (
                      <Text size='sm' color='dimmed' data-cy={`checkoutStepSummary-${item.value}`}>
                        {item.summary}
                      </Text>
                    )}
                  </div>
                </Group>
                {item.completed && !isOpen && item.onEdit && (
                  <Button
                    size='xs'
                    variant='subtle'
                    data-cy={`checkoutStepEdit-${item.value}`}
                    onClick={event => {
                      event.stopPropagation();
                      item.onEdit?.();
                    }}
                  >
                    Edit
                  </Button>
                )}
              </Group>
              {item.warning && (
                <Group spacing='xs' mt='xs' noWrap data-cy={`checkoutStepWarning-${item.value}`}>
                  <IconAlertTriangle size='1rem' color='orange' />
                  <Text size='sm' color='orange'>
                    {item.warning}
                  </Text>
                </Group>
              )}
            </Accordion.Control>
            <Accordion.Panel>{isOpen && item.children}</Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};
